import {
  DDB_RARITIES,
  DDB_BASE_TYPES,
  DDB_MAGIC_ITEM_TYPES,
  DDB_DEX_BONUS,
  DDB_STEALTH_CHECK,
  DDB_CHARGE_RESETS,
  DDB_DIE_TYPES,
  DDB_MODIFIER_TYPE_SUGGESTIONS,
} from '../../../shared/ddb-options.js'

// The "D&D Beyond" section of ItemForm: edits an item's ddb_spec, the input
// the ddb-extension importer reads. `value` is the parsed spec, or null when
// the item has no DDB stats.
//
// Edits spread over the existing objects, so a field this form doesn't show
// (e.g. a modifier's durationUnit, set by an agent through MCP) survives an
// edit here. The server (shared/ddb.js) drops blanks and validates.

const emptySpec = () => ({ item: { rarity: 'Uncommon', baseType: 'Item', magicItemType: 'Wondrous Item' }, modifiers: [], spells: [] })

const small = { fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.85rem' }

function DdbSpecEditor({ value, onChange, styles }) {
  const enabled = !!value
  const spec = value || emptySpec()
  const item = spec.item || {}
  const modifiers = spec.modifiers || []
  const spells = spec.spells || []
  const baseType = item.baseType || 'Item'

  const setItem = (patch) => onChange({ ...spec, item: { ...item, ...patch } })
  // Unticking a checkbox also clears the fields it revealed, so a hidden
  // value can't still be sent to DDB.
  const DEPENDENTS = {
    hasCharges: ['charges', 'chargeReset', 'chargeResetDescription'],
    requiresAttunement: ['attunementDescription'],
  }
  const itemField = (key) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    const patch = { [key]: v }
    if (v === false) for (const dep of DEPENDENTS[key] || []) patch[dep] = ''
    setItem(patch)
  }

  // Changing base type clears the fields that only exist for the old one —
  // the server refuses e.g. a baseWeapon on an Armor item.
  const setBaseType = (e) => {
    const next = e.target.value
    const cleared = { baseType: next }
    if (next !== 'Weapon') cleared.baseWeapon = ''
    if (next !== 'Armor') Object.assign(cleared, { baseArmor: '', dexBonus: '', strRequirement: '', stealthCheck: '' })
    if (next !== 'Item') cleared.magicItemType = ''
    setItem(cleared)
  }

  const setList = (key, list) => onChange({ ...spec, [key]: list })
  const listField = (key, list, index, field) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setList(key, list.map((entry, i) => (i === index ? { ...entry, [field]: v } : entry)))
  }

  const select = (id, current, options, onSelect, blank = true) => (
    <select id={id} value={current || ''} onChange={onSelect} style={styles.select}>
      {blank && <option value="" style={styles.option}>—</option>}
      {options.map((o) => (
        <option key={o} value={o} style={styles.option}>{o}</option>
      ))}
    </select>
  )

  const label = (htmlFor, text, hint) => (
    <label style={styles.label} htmlFor={htmlFor}>
      {text} {hint && <span style={small}>{hint}</span>}
    </label>
  )

  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }
  const rowBox = { border: '1px solid var(--border-color)', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.5rem' }
  const smallButton = {
    background: 'var(--accent-deep)',
    color: 'var(--text-primary)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    padding: '0.4rem 0.8rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  }
  const removeButton = { ...smallButton, border: '1px solid #c0392b' }

  return (
    <fieldset style={{ border: '1px solid var(--accent-teal)', borderRadius: '8px', padding: '1rem', margin: '0.5rem 0 1rem' }}>
      <legend style={{ color: 'var(--text-primary)', padding: '0 0.5rem', fontWeight: 600 }}>🐉 D&amp;D Beyond</legend>

      <label style={styles.checkbox}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onChange(e.target.checked ? emptySpec() : null)}
          style={styles.checkboxInput}
        />
        <span style={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>
          Give this item D&amp;D Beyond stats <span style={small}>(DM only — players never see this section)</span>
        </span>
      </label>

      {enabled && (
        <div style={{ marginTop: '1rem' }}>
          <div style={grid}>
            <div>
              {label('ddb-rarity', 'Rarity *')}
              {select('ddb-rarity', item.rarity, DDB_RARITIES, itemField('rarity'), false)}
            </div>
            <div>
              {label('ddb-baseType', 'Base type')}
              {select('ddb-baseType', baseType, DDB_BASE_TYPES, setBaseType, false)}
            </div>
            {baseType === 'Item' && (
              <div>
                {label('ddb-magicItemType', 'Magic item type')}
                {select('ddb-magicItemType', item.magicItemType, DDB_MAGIC_ITEM_TYPES, itemField('magicItemType'))}
              </div>
            )}
            {baseType === 'Weapon' && (
              <div>
                {label('ddb-baseWeapon', 'Base weapon', '(as DDB names it)')}
                <input id="ddb-baseWeapon" value={item.baseWeapon || ''} onChange={itemField('baseWeapon')} style={styles.input} placeholder="Longsword" />
              </div>
            )}
            {baseType === 'Armor' && (
              <div>
                {label('ddb-baseArmor', 'Base armor', '(as DDB names it)')}
                <input id="ddb-baseArmor" value={item.baseArmor || ''} onChange={itemField('baseArmor')} style={styles.input} placeholder="Chain Mail" />
              </div>
            )}
            <div>
              {label('ddb-weight', 'Weight', '(lb)')}
              <input id="ddb-weight" value={item.weight ?? ''} onChange={itemField('weight')} style={styles.input} inputMode="decimal" />
            </div>
          </div>

          {baseType === 'Armor' && (
            <div style={grid}>
              <div>
                {label('ddb-dexBonus', 'Dex bonus')}
                {select('ddb-dexBonus', item.dexBonus, DDB_DEX_BONUS, itemField('dexBonus'))}
              </div>
              <div>
                {label('ddb-strRequirement', 'Str requirement')}
                <input id="ddb-strRequirement" value={item.strRequirement ?? ''} onChange={itemField('strRequirement')} style={styles.input} inputMode="numeric" />
              </div>
              <div>
                {label('ddb-stealthCheck', 'Stealth')}
                {select('ddb-stealthCheck', item.stealthCheck, DDB_STEALTH_CHECK, itemField('stealthCheck'))}
              </div>
            </div>
          )}

          <div style={{ ...grid, alignItems: 'end' }}>
            <label style={{ ...styles.checkbox, paddingBottom: '0.75rem' }}>
              <input type="checkbox" checked={!!item.requiresAttunement} onChange={itemField('requiresAttunement')} style={styles.checkboxInput} />
              <span style={{ color: 'var(--text-primary)' }}>Requires attunement</span>
            </label>
            {item.requiresAttunement && (
              <div>
                {label('ddb-attunementDescription', 'Attunement by', '(optional)')}
                <input id="ddb-attunementDescription" value={item.attunementDescription || ''} onChange={itemField('attunementDescription')} style={styles.input} placeholder="by a spellcaster" />
              </div>
            )}
          </div>

          <div style={{ ...grid, alignItems: 'end' }}>
            <label style={{ ...styles.checkbox, paddingBottom: '0.75rem' }}>
              <input type="checkbox" checked={!!item.hasCharges} onChange={itemField('hasCharges')} style={styles.checkboxInput} />
              <span style={{ color: 'var(--text-primary)' }}>Has charges</span>
            </label>
            {item.hasCharges && (
              <>
                <div>
                  {label('ddb-charges', 'Charges')}
                  <input id="ddb-charges" value={item.charges ?? ''} onChange={itemField('charges')} style={styles.input} inputMode="numeric" />
                </div>
                <div>
                  {label('ddb-chargeReset', 'Resets on')}
                  {select('ddb-chargeReset', item.chargeReset, DDB_CHARGE_RESETS, itemField('chargeReset'))}
                </div>
              </>
            )}
          </div>
          {item.hasCharges && (
            <div style={{ marginBottom: '0.75rem' }}>
              {label('ddb-chargeResetDescription', 'Recharge text', '(optional)')}
              <input id="ddb-chargeResetDescription" value={item.chargeResetDescription || ''} onChange={itemField('chargeResetDescription')} style={styles.input} placeholder="Regains all expended charges daily at dawn." />
            </div>
          )}

          <div style={{ marginBottom: '0.75rem' }}>
            {label('ddb-tags', 'Tags', '(comma separated, DDB tag names)')}
            <input
              id="ddb-tags"
              value={(item.tags || []).join(', ')}
              onChange={(e) => setItem({ tags: e.target.value.split(',').map((t) => t.trimStart()) })}
              onBlur={() => setItem({ tags: (item.tags || []).map((t) => t.trim()).filter(Boolean) })}
              style={styles.input}
              placeholder="Damage, Combat"
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            {label('ddb-description', 'Rules text for DDB', '(optional markdown — leave blank to use Full Description)')}
            <textarea
              id="ddb-description"
              value={item.description || ''}
              onChange={itemField('description')}
              style={{ ...styles.textarea, minHeight: '90px' }}
              placeholder="You gain a +1 bonus to attack and damage rolls made with this magic weapon."
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <div style={{ ...styles.label, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Modifiers <span style={small}>(a +1 weapon is Bonus / Magic / 1)</span></span>
              <button type="button" style={smallButton} onClick={() => setList('modifiers', [...modifiers, { type: 'Bonus', subtype: '', value: '' }])}>
                + Modifier
              </button>
            </div>
            <datalist id="ddb-modifier-types">
              {DDB_MODIFIER_TYPE_SUGGESTIONS.map((t) => <option key={t} value={t} />)}
            </datalist>
            {modifiers.map((mod, i) => (
              <div key={i} style={rowBox}>
                <div style={grid}>
                  <input aria-label="Modifier type" list="ddb-modifier-types" value={mod.type || ''} onChange={listField('modifiers', modifiers, i, 'type')} style={styles.input} placeholder="Type (Bonus)" />
                  <input aria-label="Modifier subtype" value={mod.subtype || ''} onChange={listField('modifiers', modifiers, i, 'subtype')} style={styles.input} placeholder="Subtype (Magic)" />
                  <input aria-label="Modifier value" value={mod.value ?? ''} onChange={listField('modifiers', modifiers, i, 'value')} style={styles.input} placeholder="Value" />
                </div>
                <div style={{ ...grid, marginBottom: 0 }}>
                  <input aria-label="Dice count" value={mod.diceCount ?? ''} onChange={listField('modifiers', modifiers, i, 'diceCount')} style={styles.input} placeholder="Dice count" inputMode="numeric" />
                  <select aria-label="Die type" value={mod.dieType || ''} onChange={listField('modifiers', modifiers, i, 'dieType')} style={styles.select}>
                    <option value="" style={styles.option}>Die —</option>
                    {DDB_DIE_TYPES.map((d) => <option key={d} value={d} style={styles.option}>{d}</option>)}
                  </select>
                  <input aria-label="Modifier details" value={mod.details || ''} onChange={listField('modifiers', modifiers, i, 'details')} style={styles.input} placeholder="Details" />
                  <button type="button" style={removeButton} onClick={() => setList('modifiers', modifiers.filter((_, j) => j !== i))}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          <div>
            <div style={{ ...styles.label, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Spells <span style={small}>(cast from the item)</span></span>
              <button type="button" style={smallButton} onClick={() => setList('spells', [...spells, { spell: '', charges: '' }])}>
                + Spell
              </button>
            </div>
            {spells.map((sp, i) => (
              <div key={i} style={rowBox}>
                <div style={{ ...grid, marginBottom: 0, alignItems: 'center' }}>
                  <input aria-label="Spell name" value={sp.spell || ''} onChange={listField('spells', spells, i, 'spell')} style={styles.input} placeholder="Speak with Dead" />
                  <input aria-label="Charges per cast" value={sp.charges ?? ''} onChange={listField('spells', spells, i, 'charges')} style={styles.input} placeholder="Charges per cast" inputMode="numeric" />
                  <input aria-label="Save DC" value={sp.saveDc ?? ''} onChange={listField('spells', spells, i, 'saveDc')} style={styles.input} placeholder="Save DC" inputMode="numeric" />
                  <label style={styles.checkbox}>
                    <input type="checkbox" checked={!!sp.legacy} onChange={listField('spells', spells, i, 'legacy')} style={styles.checkboxInput} />
                    <span style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>2014 version</span>
                  </label>
                  <button type="button" style={removeButton} onClick={() => setList('spells', spells.filter((_, j) => j !== i))}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </fieldset>
  )
}

export default DdbSpecEditor
